import useApiMutation from 'shared/hooks/use-api-mutation';
import useInfiniteQueryHelpers from 'shared/hooks/use-infinite-query-helpers';
import useQueryHelpers from 'shared/hooks/use-query-helpers';

export interface ApiDynamicSwitchProps {
  request: {
    url: string;
    method: 'PATCH' | 'DELETE' | 'POST';
    property?: string;
    errorTitle: string;
    successTitle?: string;
    successMessage?: string;
  };
  keys: {
    details?: any;
    updateList?: any;
    updateInfiniteList?: any;
    deleteList?: any;
    deleteInfiniteList?: any;
    createInfiniteList?: any;
  };
  multipart?: boolean;
  isChecked: boolean;
}

const useApiDynamicSwitchState = ({
  request,
  keys,
  multipart,
}: ApiDynamicSwitchProps) => {
  const queryHelpers = useQueryHelpers();
  const infiniteQueryHelpers = useInfiniteQueryHelpers();

  const operationMap: Record<string, (data: any) => void> = {
    details: (data: any) => queryHelpers.setData(keys?.details as string, data),
    updateList: (data: any) =>
      queryHelpers.update(keys?.updateList as string, data),
    updateInfiniteList: (data: any) =>
      infiniteQueryHelpers.update(keys?.updateInfiniteList as string, data),
    deleteList: (data: any) =>
      queryHelpers.remove(keys?.deleteList as string, data),
    deleteInfiniteList: (data: any) =>
      infiniteQueryHelpers.remove(keys?.deleteInfiniteList as string, data),
    createInfiniteList: (data: any) =>
      infiniteQueryHelpers.create(keys?.createInfiniteList, data),
  };

  const onSuccess = (data: any) => {
    const executeOperation = (key: string) => {
      const operation = operationMap[key];
      if (operation) {
        operation(data);
      }
    };

    if (keys) {
      Object.keys(keys).forEach(executeOperation);
    }
  };

  const {
    method,
    url,
    errorTitle,
    successTitle,
    successMessage,
    property = 'active',
  } = request;

  const {mutate, isLoading} = useApiMutation({
    method,
    url,
    errorTitle,
    successTitle,
    successMessage,
    onSuccess,
  });

  const handleRequest = (isChecked: boolean) => {
    if (multipart) {
      const formData = new FormData();
      formData.append(property, String(isChecked));
      return mutate(formData);
    }
    mutate({[property]: isChecked});
  };

  return {
    handleRequest,
    isLoading,
  };
};

export default useApiDynamicSwitchState;
